import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { ClickAway } from '@components/core-ui/ClickAway';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import { FormFieldSelectLabel } from '@components/core-ui/form-elements/FormFieldSelectLabel';
import { FormFieldSelectOption } from '@components/core-ui/form-elements/FormFieldSelectOption';
import { FormFieldSelectOptionsPanel } from '@components/core-ui/form-elements/FormFieldSelectOptionsPanel';
import ClosedIcon from '@components/icons/ChevronClosed.svg';
import OpenedIcon from '@components/icons/ChevronOpened.svg';
import { cn } from '@helpers/cn';
import { optionCheckVariants, optionClassName, panelClassName, searchInputClassName, triggerVariants } from './styles';
import { TProps, TSelectOption } from './types/TProps';

export const SearchableSelect = (props: TProps) => {
  const {
    label,
    options,
    placeholder,
    disabled = false,
    error = false,
    required = false,
    size = 'md',
    className = '',
    name = 'searchable-select',
    placement = 'bottom-start',
    emptyMessage = 'No matches',
  } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [toggleAnnouncement, setToggleAnnouncement] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const componentId = useId();
  const listboxId = `${componentId}-listbox`;

  const filteredOptions = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())),
    [options, query],
  );

  const isSelected = (optionValue: string) =>
    props.multiple ? props.value.includes(optionValue) : props.value === optionValue;
  const selectedOptions = options.filter((option) => isSelected(option.value));
  const highlightedOption = filteredOptions[Math.min(highlightedIndex, Math.max(filteredOptions.length - 1, 0))];
  const optionId = (option: TSelectOption) => `${listboxId}-option-${options.indexOf(option)}`;
  const emptyLabel = placeholder ?? `${label}${required ? ' *' : ''}`;
  const currentLabel =
    selectedOptions.length === 0
      ? emptyLabel
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`;
  const triggerName = [
    currentLabel === label ? label : `${label}: ${currentLabel}`,
    ...(selectedOptions.length > 1 ? selectedOptions.map((option) => option.label) : []),
  ].join(', ');
  const state = disabled ? 'disabled' : error ? 'error' : 'default';

  useEffect(() => {
    if (open) {
      filterInputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(Math.max(filteredOptions.length - 1, 0));
    }
    const option = highlightedOption
      ? document.getElementById(`${listboxId}-option-${options.indexOf(highlightedOption)}`)
      : null;
    if (option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [filteredOptions.length, highlightedIndex, highlightedOption, listboxId, open, options]);

  const openOptions = () => {
    if (disabled) {
      return;
    }
    setQuery('');
    setHighlightedIndex(0);
    setToggleAnnouncement('');
    setOpen(true);
  };

  const closeOptions = (returnFocus = false) => {
    setOpen(false);
    setQuery('');
    setHighlightedIndex(0);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  const selectOption = (selectedValue: string) => {
    if (!props.multiple) {
      props.onChange(selectedValue);
      closeOptions(true);
      return;
    }
    const wasSelected = props.value.includes(selectedValue);
    const toggledLabel = options.find((option) => option.value === selectedValue)?.label ?? selectedValue;
    setToggleAnnouncement(`${toggledLabel} ${wasSelected ? 'removed' : 'selected'}`);
    props.onChange(
      options
        .filter((option) => (option.value === selectedValue ? !wasSelected : props.value.includes(option.value)))
        .map((option) => option.value),
    );
  };

  const selectHighlightedOption = () => {
    const option = filteredOptions[highlightedIndex];
    if (option) {
      selectOption(option.value);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) {
        selectHighlightedOption();
      } else {
        openOptions();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openOptions();
        return;
      }
      setHighlightedIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
    }
    if (event.key === 'ArrowUp' && open) {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      closeOptions(true);
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectHighlightedOption();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeOptions(true);
    }
    if (event.key === 'Tab') {
      closeOptions(true);
    }
  };

  const handleSearchBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && triggerRef.current?.contains(next)) {
      return;
    }
    closeOptions();
  };

  return (
    <FormFieldRoot disabled={disabled} className="w-full [&>div]:relative">
      <ClickAway onClickAway={() => closeOptions()}>
        <button
          ref={triggerRef}
          type="button"
          className={cn(triggerVariants({ size, state }), className)}
          onClick={open ? () => closeOptions() : openOptions}
          onKeyDown={handleTriggerKeyDown}
          data-open={open}
          data-placement={placement.startsWith('top') ? 'top' : 'bottom'}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={error || undefined}
          aria-label={triggerName}
          aria-required={required || undefined}
          disabled={disabled}
          data-name={name}
        >
          <FormFieldIcon className="icon--closed right-5 h-auto w-auto">
            {open ? (
              <OpenedIcon aria-hidden="true" focusable="false" />
            ) : (
              <ClosedIcon aria-hidden="true" focusable="false" />
            )}
          </FormFieldIcon>
          <FormFieldSelectLabel selected={selectedOptions.length > 0} disabled={disabled}>
            {currentLabel}
          </FormFieldSelectLabel>
        </button>

        {open ? (
          <FormFieldSelectOptionsPanel triggerRef={triggerRef} placement={placement} className={panelClassName}>
            <span className="optionsLabel relative mx-[15px] flex h-10 items-center border-b border-[var(--color-form-field-select-divider,var(--color-border))]">
              <input
                ref={filterInputRef}
                name={`${name}-search`}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setHighlightedIndex(0);
                  setToggleAnnouncement('');
                }}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
                placeholder={currentLabel}
                className={searchInputClassName}
                {...(props.multiple
                  ? { role: 'searchbox' }
                  : { role: 'combobox', 'aria-expanded': true, 'aria-haspopup': 'listbox' as const })}
                aria-label={label}
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={highlightedOption ? optionId(highlightedOption) : undefined}
              />
            </span>
            <span
              id={listboxId}
              className="options block max-h-[150px] overflow-y-auto"
              role="listbox"
              aria-label={label}
              aria-multiselectable={props.multiple || undefined}
            >
              {filteredOptions.map((option, index) => (
                <FormFieldSelectOption
                  key={option.value}
                  id={optionId(option)}
                  className={cn(
                    optionClassName,
                    index === highlightedIndex && 'bg-[var(--color-form-field-hover-overlay,rgba(0,0,0,0.06))]',
                  )}
                  selected={isSelected(option.value)}
                  role="option"
                  aria-selected={isSelected(option.value)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setHighlightedIndex(index);
                    selectOption(option.value);
                  }}
                >
                  {props.multiple ? (
                    <span className={optionCheckVariants({ checked: isSelected(option.value) })} aria-hidden="true">
                      {isSelected(option.value) ? '✓' : ''}
                    </span>
                  ) : null}
                  {option.label}
                </FormFieldSelectOption>
              ))}
            </span>
            {filteredOptions.length === 0 ? (
              <span className={cn(optionClassName, 'block cursor-default text-[var(--color-text-muted)]')}>
                {emptyMessage}
              </span>
            ) : null}
            <span className="sr-only" role="status" aria-live="polite">
              {toggleAnnouncement ||
                (filteredOptions.length === 0
                  ? emptyMessage
                  : `${filteredOptions.length} ${filteredOptions.length === 1 ? 'option' : 'options'} available`)}
            </span>
          </FormFieldSelectOptionsPanel>
        ) : null}
      </ClickAway>
    </FormFieldRoot>
  );
};
